import React, { useContext } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormLabel,
  IconButton,
  MenuItem,
  TextField,
} from "@mui/material";
import { MobileTimePicker } from "@mui/x-date-pickers";
import { observer } from "mobx-react-lite";
import { Controller, useForm } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import * as yup from "yup";

import { MealKind } from "@/gql/graphql";
import { isSameTimeOfDay } from "@/utils/datetime";
import { humanize } from "@/utils/string";

import MealPlanFormStore from "../../store/MealPlanFormStore";
import { PlannedMeal } from "../../store/PlannedMeal";

interface Props {
  open: boolean;
  plannedMeal?: PlannedMeal;
  onClose: () => void;
  onSubmit: (values: FormValues) => void;
}

interface FormValues {
  meal: MealKind;
  recommendedTime: Date;
}

const useStyles = makeStyles()((theme) => ({
  content: {
    display: "flex",
    flexDirection: "column",
    minWidth: 500,
  },
  actions: {
    justifyContent: "center",
    padding: theme.spacing(2, 8, 8),
  },
  closeButton: {
    backgroundColor: "#F2F2F2",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    "& .MuiFormLabel-root": {
      marginBottom: theme.spacing(3),
    },
  },
}));

const validationSchema = yup
  .object({
    meal: yup.string().label("Meal name").required("${label} is required"),
    recommendedTime: yup
      .date<Date, { existingTimes: Date[] }>()
      .label("Recommended time")
      .nullable() // so that the required validation runs when time is blank
      .typeError("${label} is not a valid time")
      .required("${label} is required")
      .test(
        "isUniqueTime",
        "A meal already exists with the same time",
        function (value) {
          if (value == null) return true;

          const existingTimes = this.options.context?.existingTimes ?? [];
          return !existingTimes.some((d) => isSameTimeOfDay(d, value));
        }
      ),
  })
  .defined();

const PlannedMealFormDialog: React.FC<Props> = observer(
  ({ open, plannedMeal, onClose, onSubmit: onSubmitProp }) => {
    const mode: "new" | "edit" = plannedMeal ? "edit" : "new";

    const { classes } = useStyles();
    const store = useContext(MealPlanFormStore.Context);
    const plannedMeals = store.rows;
    const mealOptions = plannedMeal
      ? [plannedMeal.meal, ...plannedMeals.availableMealTypes]
      : plannedMeals.availableMealTypes;
    const existingTimes = plannedMeals.existingTimes;

    const { control, handleSubmit, reset } = useForm<FormValues>({
      defaultValues:
        mode === "new"
          ? {
              meal: undefined,
              recommendedTime: new Date(),
            }
          : {
              meal: plannedMeal?.meal,
              recommendedTime: plannedMeal?.recommendedTime,
            },
      context: { existingTimes },
      resolver: yupResolver(validationSchema),
    });

    const handleClose = () => {
      onClose();
      reset();
    };

    const onSubmit = (values: FormValues) => {
      onSubmitProp(values);
      handleClose();
    };

    return (
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <span>
              {mode === "new"
                ? "Add new meal"
                : `Edit ${humanize(plannedMeal?.meal ?? "", false)}`}
            </span>
            <IconButton
              className={classes.closeButton}
              size="small"
              onClick={handleClose}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent className={classes.content}>
            <Controller
              name="meal"
              control={control}
              render={({ field, fieldState }) => (
                <div className={classes.field}>
                  <FormLabel error={!!fieldState.error}>Meal name</FormLabel>
                  <TextField
                    {...field}
                    select
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message ?? " "}
                  >
                    {mealOptions.map((mealOption) => (
                      <MenuItem key={mealOption} value={mealOption}>
                        {humanize(mealOption)}
                      </MenuItem>
                    ))}
                  </TextField>
                </div>
              )}
            />
            <Controller
              name="recommendedTime"
              control={control}
              render={({ field: { ref, onChange, ...field }, fieldState }) => (
                <div className={classes.field}>
                  <FormLabel error={!!fieldState.error}>
                    Recommended time
                  </FormLabel>
                  <MobileTimePicker
                    ref={ref}
                    onChange={(value) => onChange(value as Date)}
                    {...field}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!fieldState?.error,
                        helperText: fieldState?.error?.message,
                      },
                    }}
                  />
                </div>
              )}
            />
          </DialogContent>
          <DialogActions className={classes.actions}>
            <Button type="submit">{mode === "new" ? "Add" : "Save"}</Button>
          </DialogActions>
        </form>
      </Dialog>
    );
  }
);

export default PlannedMealFormDialog;
